import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
} from 'axios';
import { BaseRequest } from '@/api/amlbotAPI';
import {
  AlertListRequest,
  AlertListResponse,
  AlertStatsResponse,
  CompAlertStatus,
  CustomerCategoryStatsResponse,
  CustomerStatsResponse,
  TransactionStatsResponse,
  TxListRequest,
  TxListResponse,
  UpdateAlertStatusRequest,
  UpdateAlertStatusResponse,
  ComplianceCustomerRequest,
  ComplianceCustomerResponse,
  UpdateCustomerRequest,
  GetAlertLevelsSuccessResponse,
  UpdateTransactionAlertResponse,
  TxResponse,
  TxRecheckRequest,
  ResetTransactionAlertsCategoriesRequest,
  UpdateTransactionAlertRequest,
  CreateAlertRuleRequest,
  CreateAlertRuleResponse,
  ListAlertRulesRequest,
  ListAlertRulesResponse,
  UpdateAlertRuleRequest,
  UpdateAlertRuleResponse,
  DeleteAlertRuleResponse,
  DeleteAlertRuleRequest,
  ActivateAlertRuleRequest,
  ActivateAlertRuleResponse,
  DeactivateAlertRuleRequest,
  DeactivateAlertRuleResponse,
  ListsResponse,
} from './models';

const DEFAULT_CONFIG: AxiosRequestConfig = {
  baseURL: (import.meta.env.VITE_REACT_APP_COMPLIANCE_API_URL as string)!,
  withCredentials: true,
};

export type ResponseWrapper<T> = {
  result: boolean;
  data: T;
};

class ComplianceAPI {
  private readonly theAxios: AxiosInstance;

  private static instance: ComplianceAPI;

  constructor(config: AxiosRequestConfig = DEFAULT_CONFIG) {
    config.headers = config?.headers || {};
    this.theAxios = axios.create(config);
    this.initResponseInterceptors();
  }

  private initResponseInterceptors = (): void => {
    this.theAxios.interceptors.response.use(
      this.handleSuccessResponse,
      this.handleErrorResponse,
    );
  };

  private handleSuccessResponse = (response: AxiosResponse) => response;

  private handleErrorResponse = (error: AxiosError) => {
    return Promise.reject(error);
  };

  public static getInstance(
    config: AxiosRequestConfig = DEFAULT_CONFIG,
  ): ComplianceAPI {
    if (!this.instance) {
      this.instance = new ComplianceAPI(config);
    }
    return this.instance;
  }

  public updateHeader = (headerName: string, value: string) => {
    this.theAxios.defaults.headers.common[headerName] = value;
  };

  private post = <S, T>(
    endpoint: string,
    payload: S,
    config?: AxiosRequestConfig<T>,
  ) => {
    return this.theAxios
      .post<T>(endpoint, payload, config)
      .then((response: AxiosResponse): ResponseWrapper<T> => response.data);
  };

  private put = <S, T>(endpoint: string, data: S) => {
    return this.theAxios
      .put<T>(endpoint, data)
      .then((response: AxiosResponse): ResponseWrapper<T> => response.data);
  };

  private patch = <S, T>(endpoint: string, payload: S) => {
    return this.theAxios
      .patch<T>(endpoint, payload)
      .then((response: AxiosResponse): ResponseWrapper<T> => response.data);
  };

  private get = <S, T>(endpoint: string, data: S) => {
    return this.theAxios
      .get<T>(endpoint, { params: data })
      .then((response: AxiosResponse<T>) => response.data);
  };

  private delete = <T>(endpoint: string) => {
    return this.theAxios
      .delete<T>(endpoint)
      .then((response: AxiosResponse): ResponseWrapper<T> => response.data);
  };

  public listAlertLevels = (data: BaseRequest) =>
    this.get<BaseRequest, GetAlertLevelsSuccessResponse>(
      '/compliance/alertlevel',
      data,
    );

  public updateAlertLevels = (data: UpdateTransactionAlertRequest) =>
    this.put<UpdateTransactionAlertRequest, UpdateTransactionAlertResponse>(
      '/compliance/alertlevel',
      data,
    );

  public listAlerts = (data: AlertListRequest) =>
    this.get<AlertListRequest, AlertListResponse>('/compliance/alert', data);

  public updateAlertStatus = (data: UpdateAlertStatusRequest) =>
    this.put<{ status: CompAlertStatus }, UpdateAlertStatusResponse>(
      `/compliance/alert/status`,
      data,
    );

  public listTxs = (data: TxListRequest) =>
    this.get<TxListRequest, TxListResponse>('/compliance/txs', data);

  public listCustomers = (data: ComplianceCustomerRequest) =>
    this.get<ComplianceCustomerRequest, ComplianceCustomerResponse>(
      '/compliance/customers',
      data,
    );

  public updateCustomer = (data: UpdateCustomerRequest) => {
    const { id, ...body } = data;
    return this.put<UpdateCustomerRequest, ResponseWrapper<unknown>>(
      `/compliance/customers/${id}`,
      body,
    );
  };

  public getLists = () =>
    this.get<
      Record<string, never>,
      { success?: boolean; result?: boolean; data?: ListsResponse }
    >('/compliance/lists', {}).then(
      (res) => res?.data ?? (res as ListsResponse),
    );

  public getCustomerStats = (data: BaseRequest) =>
    this.get<BaseRequest, CustomerStatsResponse>(
      '/compliance/stats/customers',
      data,
    );

  public getCustomerCategoryStats = (data: BaseRequest) =>
    this.get<BaseRequest, CustomerCategoryStatsResponse>(
      '/compliance/stats/customers/by-category',
      data,
    );

  public getAlertStats = (data: BaseRequest) =>
    this.get<BaseRequest, AlertStatsResponse>('/compliance/stats/alerts', data);

  public getTransactionStats = (data: BaseRequest) =>
    this.get<BaseRequest, TransactionStatsResponse>(
      '/compliance/stats/transactions',
      data,
    );

  public txRecheck = (data: TxRecheckRequest) =>
    this.post<TxRecheckRequest, TxResponse>(`/compliance/tx/recheck`, data);

  public applyDefault = () => this.post(`/compliance/apply-default`, {});

  public resetTransactionAlertsCategories = (
    data: ResetTransactionAlertsCategoriesRequest,
  ) =>
    this.post<ResetTransactionAlertsCategoriesRequest, unknown>(
      '/compliance/alertlevel/clean/category',
      data,
    );

  // public getTransactionBriefStats() {
  //   return this.get<TransactionBriefStatsResponse>(
  //     '/api/user/stats/transactions/brief',
  //   );
  // }
  public createAlertRule = (data: CreateAlertRuleRequest) =>
    this.post<CreateAlertRuleRequest, CreateAlertRuleResponse>(
      '/compliance/rules',
      data,
    );

  public listAlertRules = (data: ListAlertRulesRequest) =>
    this.get<ListAlertRulesRequest, ListAlertRulesResponse>(
      '/compliance/rules',
      data,
    );

  public updateAlertRule = (data: UpdateAlertRuleRequest) =>
    this.post<UpdateAlertRuleRequest, UpdateAlertRuleResponse>(
      '/compliance/rules/update',
      data,
    );

  public deleteAlertRule = (data: DeleteAlertRuleRequest) =>
    this.post<DeleteAlertRuleRequest, DeleteAlertRuleResponse>(
      `/compliance/rules/delete`,
      data,
    );

  public activateAlertRule = (data: ActivateAlertRuleRequest) =>
    this.post<ActivateAlertRuleRequest, ActivateAlertRuleResponse>(
      `/compliance/rules/activate`,
      data,
    );

  public deactivateAlertRule = (data: DeactivateAlertRuleRequest) =>
    this.post<DeactivateAlertRuleRequest, DeactivateAlertRuleResponse>(
      `/compliance/rules/deactivate`,
      data,
    );
}

const complianceAPI = ComplianceAPI.getInstance();

export default complianceAPI;
